/**
 * MutualFundsTab.jsx — Mutual Funds / Index Funds portfolio tab.
 *
 * Shows mutual fund holdings (from broker sync or manual import),
 * overview KPIs, holdings table, and import CTA.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
    getPortfolioOverview,
    getPortfolioXIRR,
    getAllocation,
    getHoldings,
    getSyncStatus,
    triggerSync,
    importMFHoldings,
} from '../../services/portfolioService';
import { searchFunds, getFundAnalytics } from '../../services/fundsService';

/* ── helpers ── */
const fmt = (n, decimals = 2) => {
    if (n == null || isNaN(n)) return 'N/A';
    return Number(n).toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};
const fmtCurrency = (n) => (n == null || isNaN(n) ? 'N/A' : `₹${fmt(n)}`);
const fmtPct = (n) => (n == null || isNaN(n) ? 'N/A' : `${fmt(n)}%`);
const pnlClass = (n) => (n > 0 ? 'pnl-positive' : n < 0 ? 'pnl-negative' : '');
const pnlKpiClass = (n) => (n > 0 ? 'portfolio-kpi-value--positive' : n < 0 ? 'portfolio-kpi-value--negative' : '');
const relativeTime = (d) => {
    if (!d) return '';
    const ms = Date.now() - new Date(d).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
};

const LANE = 'MF_INDEX_FUNDS';

const isIndexFundHolding = (holding) => {
    const rawName = `${holding?.displayName || ''} ${holding?.name || ''} ${holding?.instrumentId || ''}`.toLowerCase();
    return (
        rawName.includes('index') ||
        rawName.includes('nifty') ||
        rawName.includes('sensex') ||
        rawName.includes('bees') ||
        rawName.includes('etf') ||
        rawName.includes('nasdaq') ||
        rawName.includes('s&p') ||
        rawName.includes('sp500')
    );
};

const INDEX_SPLIT_COLORS = {
    'Index Funds': '#3b82f6',
    'Mutual Funds': '#7c3aed',
};

const normaliseSchemeName = (value) => (value || '')
    .toLowerCase()
    .replace(/[-_/(),.]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const buildSearchQueries = (displayName) => {
    const normalized = normaliseSchemeName(displayName);
    const cleaned = normalized
        .replace(/\bdirect\b/g, ' ')
        .replace(/\bgrowth\b/g, ' ')
        .replace(/\bregular\b/g, ' ')
        .replace(/\bplan\b/g, ' ')
        .replace(/\boption\b/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const tokens = cleaned.split(' ').filter(Boolean);
    const shortQuery = tokens.slice(0, 4).join(' ');
    const issuerQuery = tokens.slice(0, 2).join(' ');

    return Array.from(new Set([normalized, cleaned, shortQuery, issuerQuery].filter((q) => q.length >= 2)));
};

const scoreCandidate = (candidate, targetName) => {
    const candidateName = normaliseSchemeName(candidate?.schemeName || candidate?.name || '');
    const target = normaliseSchemeName(targetName);
    if (!candidateName || !target) return 0;

    if (candidateName === target) return 1000;
    if (candidateName.includes(target) || target.includes(candidateName)) return 900;

    const targetTokens = target.split(' ').filter(Boolean);
    const candidateTokens = new Set(candidateName.split(' ').filter(Boolean));
    const overlap = targetTokens.filter((t) => candidateTokens.has(t)).length;
    return overlap * 10 + Math.min(candidateName.length, target.length) / 100;
};

const IndexSplitTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div className="sector-tooltip">
            <div className="sector-tooltip-header">
                <span className="sector-tooltip-dot" style={{ background: d.color }} />
                <strong>{d.name}</strong>
            </div>
            <div className="sector-tooltip-row">
                <span>Value</span>
                <span>{fmtCurrency(d.value)}</span>
            </div>
            <div className="sector-tooltip-row">
                <span>Weight</span>
                <span>{fmtPct(d.pct)}</span>
            </div>
            <div className="sector-tooltip-row">
                <span>Holdings</span>
                <span>{d.count}</span>
            </div>
        </div>
    );
};

const MutualFundsTab = () => {
    const [overview, setOverview] = useState(null);
    const [xirr, setXirr] = useState(null);
    const [holdings, setHoldings] = useState([]);
    const [allocation, setAllocation] = useState(null);
    const [syncStatus, setSyncStatus] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [importJson, setImportJson] = useState('');
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [editingHolding, setEditingHolding] = useState(null);
    const [editForm, setEditForm] = useState(null);
    const [savingEdit, setSavingEdit] = useState(false);
    const [analyticsTarget, setAnalyticsTarget] = useState(null);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [analyticsError, setAnalyticsError] = useState('');
    const fileInputRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [ov, xi, hld, al, ss] = await Promise.allSettled([
                getPortfolioOverview(LANE),
                getPortfolioXIRR(LANE),
                getHoldings(LANE),
                getAllocation(LANE),
                getSyncStatus(),
            ]);
            if (ov.status === 'fulfilled') setOverview(ov.value?.data ?? ov.value);
            if (xi.status === 'fulfilled') setXirr(xi.value?.data ?? xi.value);
            if (hld.status === 'fulfilled') setHoldings(hld.value?.data ?? hld.value ?? []);
            if (al.status === 'fulfilled') setAllocation(al.value?.data ?? al.value);
            if (ss.status === 'fulfilled') setSyncStatus(ss.value?.data ?? ss.value);
        } catch { /* ignore */ }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleSync = async () => {
        setSyncing(true);
        try {
            await triggerSync();
            setTimeout(load, 3000);
        } catch { /* ignore */ }
        setSyncing(false);
    };

    /* ── MF Import handlers ── */
    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const text = ev.target.result;
            try {
                // Try parsing as JSON
                const parsed = JSON.parse(text);
                setImportJson(JSON.stringify(parsed, null, 2));
            } catch {
                // Could be CSV — show raw text for user to fix
                setImportJson(text);
            }
        };
        reader.readAsText(file);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleImport = async () => {
        setImporting(true);
        setImportResult(null);
        try {
            let holdings;
            try {
                const parsed = JSON.parse(importJson);
                holdings = Array.isArray(parsed) ? parsed : parsed.holdings || [parsed];
            } catch {
                setImportResult({ error: 'Invalid JSON. Please provide an array of MF holdings.' });
                setImporting(false);
                return;
            }
            const res = await importMFHoldings(holdings);
            setImportResult(res?.data ?? res);
            load(); // refresh
        } catch (err) {
            setImportResult({ error: err?.response?.data?.error || err.message || 'Import failed' });
        }
        setImporting(false);
    };

    const openEditModal = (holding) => {
        setEditingHolding(holding);
        setEditForm({
            schemeName: holding.displayName || holding.name || '',
            instrumentId: holding.instrumentId,
            units: holding.quantity ?? 0,
            avgNav: holding.avgCost ?? 0,
            currentNav: holding.lastPrice ?? 0,
            investedValue: holding.valueAtCost ?? ((holding.quantity || 0) * (holding.avgCost || 0)),
            currentValue: holding.marketValue ?? ((holding.quantity || 0) * (holding.lastPrice || 0)),
            isin: holding.instrumentId?.startsWith('IN') ? holding.instrumentId : '',
            xirr: holding.xirr ?? '',
        });
    };

    const handleEditChange = (field, value) => {
        setEditForm((prev) => {
            const next = { ...prev, [field]: value };
            const units = Number(next.units) || 0;
            const avgNav = Number(next.avgNav) || 0;
            const currentNav = Number(next.currentNav) || 0;

            if (field === 'units' || field === 'avgNav') {
                next.investedValue = (units * avgNav).toFixed(2);
            }
            if (field === 'units' || field === 'currentNav') {
                next.currentValue = (units * currentNav).toFixed(2);
            }
            return next;
        });
    };

    const saveEditedHolding = async () => {
        if (!editForm) return;
        setSavingEdit(true);
        try {
            const payload = {
                schemeName: editForm.schemeName,
                instrumentId: editForm.instrumentId,
                isin: editForm.isin,
                units: Number(editForm.units) || 0,
                avgNav: Number(editForm.avgNav) || 0,
                currentNav: Number(editForm.currentNav) || 0,
                investedValue: Number(editForm.investedValue) || 0,
                currentValue: Number(editForm.currentValue) || 0,
                xirr: editForm.xirr === '' ? null : Number(editForm.xirr),
            };
            await importMFHoldings([payload]);
            setEditingHolding(null);
            setEditForm(null);
            await load();
        } catch (err) {
            setImportResult({ error: err?.response?.data?.error || err.message || 'Failed to save holding edits' });
        }
        setSavingEdit(false);
    };

    const trackHoldingAnalytics = async (holding) => {
        const displayName = holding.displayName || holding.name || '';
        setAnalyticsTarget(holding);
        setAnalyticsData(null);
        setAnalyticsError('');
        setAnalyticsLoading(true);
        try {
            const queries = buildSearchQueries(displayName);
            const mergedCandidates = [];

            for (const query of queries) {
                try {
                    const searchRes = await searchFunds(query);
                    const list = searchRes?.data ?? searchRes ?? [];
                    if (Array.isArray(list)) {
                        mergedCandidates.push(...list);
                    }
                } catch {
                    // continue fallback queries
                }
                if (mergedCandidates.length >= 25) break;
            }

            const deduped = [];
            const seen = new Set();
            for (const c of mergedCandidates) {
                const key = String(c?.schemeCode || c?.code || c?.value || c?.schemeName || '');
                if (!key || seen.has(key)) continue;
                seen.add(key);
                deduped.push(c);
            }

            if (!deduped.length) {
                throw new Error('No scheme match found in funds analytics catalog');
            }

            const ranked = deduped
                .map((candidate) => ({ candidate, score: scoreCandidate(candidate, displayName) }))
                .sort((a, b) => b.score - a.score);

            const matched = ranked[0]?.candidate;
            const schemeCode = matched.schemeCode || matched.code || matched.value;
            if (!schemeCode) {
                throw new Error('Matched scheme has no schemeCode');
            }
            const analyticsRes = await getFundAnalytics(schemeCode, 'NIFTY_50');
            const analytics = analyticsRes?.data ?? analyticsRes;
            setAnalyticsData({ matched, analytics });
        } catch (err) {
            setAnalyticsError(err?.response?.data?.error || err.message || 'Failed to load analytics');
        }
        setAnalyticsLoading(false);
    };

    if (loading) return <div className="portfolio-loading">Loading Mutual Funds…</div>;

    const hasData = holdings.length > 0;
    const totalMfValue = holdings.reduce((sum, h) => sum + (h.marketValue || 0), 0);

    const indexFunds = holdings.filter(isIndexFundHolding);
    const mutualFunds = holdings.filter((h) => !isIndexFundHolding(h));

    const indexValue = indexFunds.reduce((sum, h) => sum + (h.marketValue || 0), 0);
    const mutualValue = mutualFunds.reduce((sum, h) => sum + (h.marketValue || 0), 0);

    const indexSplitData = [
        {
            name: 'Index Funds',
            value: indexValue,
            pct: totalMfValue > 0 ? (indexValue / totalMfValue) * 100 : 0,
            count: indexFunds.length,
            color: INDEX_SPLIT_COLORS['Index Funds'],
        },
        {
            name: 'Mutual Funds',
            value: mutualValue,
            pct: totalMfValue > 0 ? (mutualValue / totalMfValue) * 100 : 0,
            count: mutualFunds.length,
            color: INDEX_SPLIT_COLORS['Mutual Funds'],
        },
    ];

    return (
        <>
            {/* Sync bar */}
            <div className="portfolio-sync-bar">
                <span className={`sync-status-dot sync-status-dot--${syncStatus?.overallHealth ?? 'disconnected'}`} />
                <span className="sync-label">
                    {syncStatus?.lastSyncAt ? `Last synced ${relativeTime(syncStatus.lastSyncAt)}` : 'Not synced yet'}
                </span>
                <button className="sync-btn" onClick={handleSync} disabled={syncing}>
                    {syncing ? 'Syncing…' : '⟳ Sync now'}
                </button>
                <button className="sync-btn" onClick={() => setImportModalOpen(true)} style={{ marginLeft: '0.5rem' }}>
                    📥 Import MF Holdings
                </button>
            </div>

            {/* KPI cards */}
            {overview ? (
                <div className="portfolio-kpi-row">
                    <div className="portfolio-kpi">
                        <div className="portfolio-kpi-label">Current Value</div>
                        <div className="portfolio-kpi-value">{fmtCurrency(overview.currentValue)}</div>
                    </div>
                    <div className="portfolio-kpi">
                        <div className="portfolio-kpi-label">Invested Value</div>
                        <div className="portfolio-kpi-value">{fmtCurrency(overview.totalCost)}</div>
                    </div>
                    <div className="portfolio-kpi">
                        <div className="portfolio-kpi-label">Total P&L</div>
                        <div className={`portfolio-kpi-value ${pnlKpiClass(overview.totalPnl)}`}>{fmtCurrency(overview.totalPnl)}</div>
                        <div className="portfolio-kpi-sub">{fmtPct(overview.totalPnlPct)}</div>
                    </div>
                    {xirr?.portfolioXIRR != null && (
                        <div className="portfolio-kpi">
                            <div className="portfolio-kpi-label">MF XIRR</div>
                            <div className={`portfolio-kpi-value ${pnlKpiClass(xirr.portfolioXIRR)}`}>{fmtPct(xirr.portfolioXIRR)}</div>
                        </div>
                    )}
                </div>
            ) : !hasData ? (
                <div className="portfolio-empty">
                    <div className="portfolio-empty-icon">📈</div>
                    <div className="portfolio-empty-title">No mutual fund holdings yet</div>
                    <p>Mutual fund data is not available via broker sync for most brokers.</p>
                    <button
                        className="broker-action-btn broker-action-btn--primary"
                        onClick={() => setImportModalOpen(true)}
                        style={{ marginTop: '1rem' }}
                    >
                        📥 Import Mutual Funds
                    </button>
                    <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '0.5rem' }}>
                        Upload your CAS statement or a JSON/CSV with scheme details.
                    </p>
                </div>
            ) : null}

            {/* Allocation breakdown within MF */}
            {allocation?.assetClass?.length > 0 && (
                <section className="portfolio-section">
                    <h3 className="portfolio-section-title">Allocation by Category</h3>
                    <div className="portfolio-kpi-row">
                        {allocation.assetClass.map((ac) => (
                            <div className="portfolio-kpi" key={ac.name}>
                                <div className="portfolio-kpi-label">{ac.name}</div>
                                <div className="portfolio-kpi-value">{fmtCurrency(ac.value)}</div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Index vs Mutual Fund split */}
            {hasData && totalMfValue > 0 && (
                <section className="portfolio-section">
                    <h3 className="portfolio-section-title">Index Funds vs Mutual Funds</h3>
                    <p className="sector-subtitle">Split based on scheme names (e.g., Nifty/Index/BEES/ETF treated as Index Funds)</p>
                    <div className="sector-chart-container">
                        <div className="sector-pie-wrap" style={{ minHeight: 360, flexBasis: 420 }}>
                            <ResponsiveContainer width="100%" height={360}>
                                <PieChart>
                                    <Pie
                                        data={indexSplitData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={110}
                                        dataKey="value"
                                        nameKey="name"
                                        label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
                                        labelLine={{ stroke: 'var(--text-3)', strokeWidth: 1 }}
                                        minAngle={4}
                                    >
                                        {indexSplitData.map((entry) => (
                                            <Cell key={entry.name} fill={entry.color} stroke="var(--bg-card)" strokeWidth={2} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<IndexSplitTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="sector-center-label">
                                <div className="sector-center-value">{fmtCurrency(totalMfValue)}</div>
                                <div className="sector-center-sub">MF Total Value</div>
                            </div>
                        </div>

                        <div className="sector-legend-wrap" style={{ maxHeight: 360 }}>
                            <ul className="sector-legend">
                                {indexSplitData.map((entry) => (
                                    <li key={entry.name} className="sector-legend-item" style={{ cursor: 'default' }}>
                                        <span className="sector-legend-dot" style={{ background: entry.color }} />
                                        <span className="sector-legend-label">{entry.name}</span>
                                        <span className="sector-legend-pct">{fmtPct(entry.pct)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>
            )}

            {/* Holdings table */}
            {hasData && (
                <section className="portfolio-section portfolio-section--full">
                    <h3 className="portfolio-section-title">Mutual Fund Holdings</h3>
                    <div className="holdings-table-wrap">
                        <table className="holdings-table">
                            <thead>
                                <tr>
                                    <th>Scheme / Fund Name</th>
                                    <th>Type</th>
                                    <th className="text-right">Units</th>
                                    <th className="text-right">Avg NAV / Cost</th>
                                    <th className="text-right">Current NAV</th>
                                    <th className="text-right">Invested Value</th>
                                    <th className="text-right">Current Value</th>
                                    <th className="text-right">P&L</th>
                                    <th className="text-right">P&L %</th>
                                    <th className="text-right">Weight</th>
                                    <th className="text-right">XIRR</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {holdings.map((h) => {
                                    const isIndex = isIndexFundHolding(h);
                                    const invested = h.valueAtCost ?? ((h.quantity || 0) * (h.avgCost || 0));
                                    const currentVal = h.marketValue ?? 0;
                                    const absPnl = h.unrealizedPnl ?? (currentVal - invested);
                                    const pnlPct = invested ? (absPnl / invested) * 100 : null;

                                    return (
                                        <tr key={h.instrumentId}>
                                            <td style={{ fontWeight: 600 }}>
                                                {h.displayName || h.name || h.instrumentId}
                                            </td>
                                            <td>
                                                <span className={`lane-pill ${isIndex ? 'lane-pill--index' : 'lane-pill--mf'}`} title={isIndex ? 'Index Fund' : 'Mutual Fund'}>
                                                    {isIndex ? 'INDEX' : 'MF'}
                                                </span>
                                            </td>
                                            <td className="text-right">{fmt(h.quantity, 3)}</td>
                                            <td className="text-right">{fmtCurrency(h.avgCost)}</td>
                                            <td className="text-right">{fmtCurrency(h.lastPrice)}</td>
                                            <td className="text-right">{fmtCurrency(invested)}</td>
                                            <td className="text-right">{fmtCurrency(currentVal)}</td>
                                            <td className={`text-right ${pnlClass(absPnl)}`}>{fmtCurrency(absPnl)}</td>
                                            <td className={`text-right ${pnlClass(pnlPct)}`}>{fmtPct(pnlPct)}</td>
                                            <td className="text-right">{fmtPct(h.globalWeightPct ?? h.weight)}</td>
                                            <td className={`text-right ${pnlClass(h.xirr)}`}>{h.xirr != null ? fmtPct(h.xirr) : 'N/A'}</td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                <button className="broker-action-btn broker-action-btn--secondary" style={{ fontSize: '0.72rem', marginRight: 6 }} onClick={() => openEditModal(h)}>
                                                    Edit
                                                </button>
                                                <button className="broker-action-btn broker-action-btn--secondary" style={{ fontSize: '0.72rem' }} onClick={() => trackHoldingAnalytics(h)}>
                                                    Track
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* Holding analytics modal */}
            {analyticsTarget && (
                <div className="broker-connect-modal-overlay" onClick={() => setAnalyticsTarget(null)}>
                    <div className="broker-connect-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 700 }}>
                        <h3>Fund Analytics — {analyticsTarget.displayName || analyticsTarget.name}</h3>
                        {analyticsLoading && <div className="portfolio-loading">Loading analytics…</div>}
                        {!analyticsLoading && analyticsError && (
                            <p style={{ color: 'var(--red)', fontSize: '0.85rem' }}>{analyticsError}</p>
                        )}
                        {!analyticsLoading && analyticsData?.analytics && (
                            <div className="portfolio-kpi-row" style={{ marginTop: '0.75rem' }}>
                                <div className="portfolio-kpi"><div className="portfolio-kpi-label">CAGR 3Y</div><div className="portfolio-kpi-value">{fmtPct(analyticsData.analytics?.cagr_3y)}</div></div>
                                <div className="portfolio-kpi"><div className="portfolio-kpi-label">CAGR 5Y</div><div className="portfolio-kpi-value">{fmtPct(analyticsData.analytics?.cagr_5y)}</div></div>
                                <div className="portfolio-kpi"><div className="portfolio-kpi-label">Sharpe</div><div className="portfolio-kpi-value">{analyticsData.analytics?.sharpe ?? 'N/A'}</div></div>
                                <div className="portfolio-kpi"><div className="portfolio-kpi-label">Beta</div><div className="portfolio-kpi-value">{analyticsData.analytics?.beta ?? 'N/A'}</div></div>
                            </div>
                        )}
                        <div className="modal-actions">
                            <button className="broker-action-btn broker-action-btn--secondary" onClick={() => setAnalyticsTarget(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit modal */}
            {editingHolding && editForm && (
                <div className="broker-connect-modal-overlay" onClick={() => { setEditingHolding(null); setEditForm(null); }}>
                    <div className="broker-connect-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 680 }}>
                        <h3>Edit Mutual Fund Holding</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                            <div>
                                <label>Scheme Name</label>
                                <input value={editForm.schemeName} onChange={(e) => handleEditChange('schemeName', e.target.value)} />
                            </div>
                            <div>
                                <label>ISIN (optional)</label>
                                <input value={editForm.isin} onChange={(e) => handleEditChange('isin', e.target.value)} />
                            </div>
                            <div>
                                <label>Units</label>
                                <input type="number" value={editForm.units} onChange={(e) => handleEditChange('units', e.target.value)} />
                            </div>
                            <div>
                                <label>Avg NAV / Cost</label>
                                <input type="number" value={editForm.avgNav} onChange={(e) => handleEditChange('avgNav', e.target.value)} />
                            </div>
                            <div>
                                <label>Current NAV</label>
                                <input type="number" value={editForm.currentNav} onChange={(e) => handleEditChange('currentNav', e.target.value)} />
                            </div>
                            <div>
                                <label>XIRR (%) optional</label>
                                <input type="number" value={editForm.xirr} onChange={(e) => handleEditChange('xirr', e.target.value)} />
                            </div>
                            <div>
                                <label>Invested Value</label>
                                <input type="number" value={editForm.investedValue} onChange={(e) => handleEditChange('investedValue', e.target.value)} />
                            </div>
                            <div>
                                <label>Current Value</label>
                                <input type="number" value={editForm.currentValue} onChange={(e) => handleEditChange('currentValue', e.target.value)} />
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button className="broker-action-btn broker-action-btn--secondary" onClick={() => { setEditingHolding(null); setEditForm(null); }}>
                                Cancel
                            </button>
                            <button className="broker-action-btn broker-action-btn--primary" onClick={saveEditedHolding} disabled={savingEdit}>
                                {savingEdit ? 'Saving…' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Import modal */}
            {importModalOpen && (
                <div className="broker-connect-modal-overlay" onClick={() => setImportModalOpen(false)}>
                    <div className="broker-connect-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
                        <h3>Import Mutual Fund Holdings</h3>
                        <p style={{ fontSize: '0.82rem', color: 'var(--text-2)', margin: '0 0 0.75rem' }}>
                            Paste a JSON array or upload a file. Each entry should have:<br />
                            <code>schemeName, units, avgNav</code> (required), <code>currentNav, isin, folioNumber, xirr</code> (optional).
                        </p>

                        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                            <button className="broker-action-btn broker-action-btn--secondary" onClick={() => fileInputRef.current?.click()}>
                                Upload JSON/CSV
                            </button>
                            <input ref={fileInputRef} type="file" accept=".json,.csv,.txt" style={{ display: 'none' }} onChange={handleFileUpload} />
                        </div>

                        <textarea
                            value={importJson}
                            onChange={(e) => setImportJson(e.target.value)}
                            rows={10}
                            style={{
                                width: '100%',
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                                padding: '0.5rem',
                                borderRadius: 6,
                                border: '1px solid var(--border)',
                                background: 'var(--surface-1)',
                                color: 'var(--text-1)',
                                resize: 'vertical',
                            }}
                            placeholder={`[\n  {\n    "schemeName": "Axis Bluechip Fund - Growth",\n    "units": 100.5,\n    "avgNav": 45.20,\n    "currentNav": 52.10,\n    "isin": "INF846K01EW2"\n  }\n]`}
                        />

                        {importResult && (
                            <div style={{
                                margin: '0.5rem 0',
                                padding: '0.5rem',
                                borderRadius: 6,
                                background: importResult.error ? 'var(--red-bg, #fef2f2)' : 'var(--green-bg, #f0fdf4)',
                                color: importResult.error ? 'var(--red)' : 'var(--green, #16a34a)',
                                fontSize: '0.85rem',
                            }}>
                                {importResult.error
                                    ? `Error: ${importResult.error}`
                                    : `Imported ${importResult.imported} of ${importResult.total} holdings (${importResult.skipped} skipped)`
                                }
                            </div>
                        )}

                        <div className="modal-actions">
                            <button className="broker-action-btn broker-action-btn--secondary" onClick={() => { setImportModalOpen(false); setImportResult(null); }}>
                                Cancel
                            </button>
                            <button className="broker-action-btn broker-action-btn--primary" onClick={handleImport} disabled={importing || !importJson.trim()}>
                                {importing ? 'Importing…' : 'Import'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default MutualFundsTab;
